/**
 * Merges the three destination autocomplete sources (cities, regions/states, countries)
 * into a single relevance-ranked list for the unified destination search input.
 *
 * The backend has no combined endpoint — cities come from /location/autocomplete/google
 * (filterMode=cities) and regions/countries from /destinations/autocomplete, one request
 * per type. Each list arrives ordered by Google's own prominence, but there is no
 * cross-type prominence signal, so ordering across the three lists is decided here.
 */

import type { CitySearchResult, DestinationAutocompleteResult } from '@/types/city';
import {
  getAutocompleteResultDisplayLabels,
  getCityResultDisplayLabels,
  isCitySearchResult,
} from '@/utils/destinationPickerUtils';

export type AnyDestinationResult = CitySearchResult | DestinationAutocompleteResult;

/** Maximum rows shown in the merged dropdown. */
export const DESTINATION_RESULT_LIMIT = 10;

/**
 * Rows guaranteed to each non-empty source before the remaining slots are filled by
 * score. Without this a long city list would push regions and countries off the end.
 */
export const DESTINATION_RESERVED_PER_TYPE = 2;

/** Lower sorts first. Cities are the common case, so they win an otherwise-equal tie. */
const TYPE_PRIORITY: Record<string, number> = {
  city: 0,
  locality: 1,
  country: 2,
};

function resultType(result: AnyDestinationResult): string {
  return isCitySearchResult(result) ? 'city' : result.type;
}

function resultLabel(result: AnyDestinationResult): string {
  return isCitySearchResult(result)
    ? getCityResultDisplayLabels(result).label
    : getAutocompleteResultDisplayLabels(result).label;
}

/** exact 3 > prefix 2 > contains 1 > no match 0. */
function matchScore(label: string, normalizedQuery: string): number {
  if (!normalizedQuery) return 0;
  const normalizedLabel = label.trim().toLowerCase();
  if (!normalizedLabel) return 0;
  if (normalizedLabel === normalizedQuery) return 3;
  if (normalizedLabel.startsWith(normalizedQuery)) return 2;
  if (normalizedLabel.includes(normalizedQuery)) return 1;
  return 0;
}

interface ScoredResult {
  result: AnyDestinationResult;
  score: number;
  typeRank: number;
  sourceIndex: number;
}

function compareScored(a: ScoredResult, b: ScoredResult): number {
  if (a.score !== b.score) return b.score - a.score;
  if (a.typeRank !== b.typeRank) return a.typeRank - b.typeRank;
  return a.sourceIndex - b.sourceIndex;
}

function score(results: AnyDestinationResult[], normalizedQuery: string): ScoredResult[] {
  return results.map((result, sourceIndex) => ({
    result,
    score: matchScore(resultLabel(result), normalizedQuery),
    typeRank: TYPE_PRIORITY[resultType(result)] ?? TYPE_PRIORITY.country,
    sourceIndex,
  }));
}

/**
 * Dedupe key. Google place ids are the reliable identity; fall back to type + label so
 * two sources returning the same place without an id still collapse to one row.
 */
function dedupeKey(result: AnyDestinationResult): string {
  if (result.google_place_id) return `gpid:${result.google_place_id}`;
  return `${resultType(result)}:${resultLabel(result).trim().toLowerCase()}`;
}

/**
 * Merges and ranks the three autocomplete sources into one dropdown list.
 *
 * Ranking: match quality (exact > prefix > contains), then city > region/state > country,
 * then the source's own order. Duplicate places are collapsed keeping the
 * highest-priority type — a city row beats a locality row for city-states like Singapore,
 * and it is the row that carries the photo and the rich CitySearchResult payload.
 */
export function mergeAndRankDestinationResults(
  query: string,
  cities: CitySearchResult[],
  regions: DestinationAutocompleteResult[],
  countries: DestinationAutocompleteResult[],
  limit: number = DESTINATION_RESULT_LIMIT,
): AnyDestinationResult[] {
  const normalizedQuery = query.trim().toLowerCase();

  const scoredBySource = [
    score(cities, normalizedQuery),
    score(regions, normalizedQuery),
    score(countries, normalizedQuery),
  ].map((group) => [...group].sort(compareScored));

  const seen = new Set<string>();
  const picked: ScoredResult[] = [];

  const take = (candidate: ScoredResult): boolean => {
    const key = dedupeKey(candidate.result);
    if (seen.has(key)) return false;
    seen.add(key);
    picked.push(candidate);
    return true;
  };

  // Reserve the top rows of each source first so no type gets squeezed out entirely.
  for (const group of scoredBySource) {
    let reserved = 0;
    for (const candidate of group) {
      if (reserved >= DESTINATION_RESERVED_PER_TYPE || picked.length >= limit) break;
      if (take(candidate)) reserved += 1;
    }
  }

  // Fill the remaining slots purely by rank.
  const remaining = scoredBySource.flat().sort(compareScored);
  for (const candidate of remaining) {
    if (picked.length >= limit) break;
    take(candidate);
  }

  return picked.sort(compareScored).map((entry) => entry.result);
}
